/**
 * Éric/Vincent #168 (2026-05-24) — Coupons de réduction par organisation.
 *
 * Page accessible depuis Plateforme Knoria > Personnes > Entreprise > Coupons
 * (ou via /dashboard/billing/coupons). Permet à l'admin/fondateur de :
 *   - créer un coupon (code, nom, % réduction, durée en mois ou illimité)
 *   - activer/désactiver, supprimer
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useLang } from "../contexts/LanguageContext";
import { Plus, Trash, Tag, X, FloppyDisk, Users, PencilSimple, CheckCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
function L(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function CouponsPage() {
  const { lang } = useLang();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // Vincent (Eric 2026-02-06) — Modal "Gérer les utilisateurs qui ont ce coupon"
  const [managing, setManaging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/coupons`, { withCredentials: true });
      setCoupons(r.data.coupons || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec du chargement", "Load failed"));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    const ok = await confirmDialog(L(lang, `Supprimer le coupon "${c.code}" ?`, `Delete coupon "${c.code}"?`));
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/coupons/${c.id}`, { withCredentials: true });
      toast.success(L(lang, "Coupon supprimé", "Coupon deleted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/api/coupons/${c.id}`, { is_active: !c.is_active }, { withCredentials: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="coupons-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900 flex items-center gap-2">
            <Tag size={28} weight="duotone" /> {L(lang, "Coupons de réduction", "Discount Coupons")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {L(lang, "Créez des coupons applicables à votre abonnement Knoria.",
                     "Create coupons applicable to your Knoria subscription.")}
          </p>
        </div>
        <button
          onClick={() => setEditing({ id: null })}
          className="px-4 py-2 bg-[#002FA7] text-white rounded font-semibold inline-flex items-center gap-2"
          data-testid="btn-new-coupon"
        >
          <Plus size={16} weight="bold" /> {L(lang, "Nouveau coupon", "New coupon")}
        </button>
      </header>

      {loading ? (
        <div className="text-center py-12 text-zinc-400">{L(lang, "Chargement…", "Loading…")}</div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-16 bg-zinc-50 rounded-xl border-2 border-dashed border-zinc-200">
          <p className="text-zinc-500 mb-3">{L(lang, "Aucun coupon créé.", "No coupons yet.")}</p>
          <button onClick={() => setEditing({ id: null })} className="text-[#002FA7] hover:underline font-medium">
            {L(lang, "Créer un premier coupon →", "Create first coupon →")}
          </button>
        </div>
      ) : (
        <table className="w-full bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="coupons-table">
          <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">{L(lang, "Nom", "Name")}</th>
              <th className="px-3 py-2 text-right">%</th>
              <th className="px-3 py-2 text-right">{L(lang, "Durée", "Duration")}</th>
              <th className="px-3 py-2 text-right">{L(lang, "Util.", "Uses")}</th>
              <th className="px-3 py-2 text-center">{L(lang, "Actif", "Active")}</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`coupon-row-${c.code}`}>
                <td className="px-3 py-2 font-mono text-sm font-bold text-[#002FA7]">{c.code}</td>
                <td className="px-3 py-2 text-sm">{c.name}</td>
                <td className="px-3 py-2 text-right font-semibold text-teal-600">−{c.discount_percent}%</td>
                <td className="px-3 py-2 text-right text-xs text-zinc-600">
                  {c.duration_months
                    ? `${c.duration_months} ${L(lang, "mois", "mo")}`
                    : c.duration_weeks
                      ? `${c.duration_weeks} ${L(lang, "semaines", "wk")}`
                      : <span className="text-violet-600 font-medium">{L(lang, "Illimité", "Forever")}</span>}
                </td>
                <td className="px-3 py-2 text-right text-xs text-zinc-500">
                  {c.current_uses}{c.max_uses ? ` / ${c.max_uses}` : ""}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => toggleActive(c)}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${c.is_active ? "bg-teal-100 text-teal-700" : "bg-zinc-200 text-zinc-500"}`}
                    data-testid={`coupon-toggle-${c.code}`}
                  >
                    {c.is_active ? L(lang, "Actif", "Active") : L(lang, "Inactif", "Off")}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  {/* Vincent (Eric 2026-02-06) — Gérer les utilisateurs qui ont ce coupon */}
                  <button
                    onClick={() => setManaging(c)}
                    className="p-1 text-violet-600 hover:bg-violet-50 rounded mr-1"
                    data-testid={`coupon-manage-users-${c.code}`}
                    title={L(lang, "Gérer les utilisateurs", "Manage users")}
                  >
                    <Users size={14} />
                  </button>
                  <button
                    onClick={() => setEditing(c)}
                    className="p-1 text-zinc-600 hover:bg-zinc-100 rounded mr-1"
                    data-testid={`coupon-edit-${c.code}`}
                    title={L(lang, "Éditer", "Edit")}
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => remove(c)} className="p-1 text-rose-500 hover:bg-rose-50 rounded" data-testid={`coupon-delete-${c.code}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <CouponEditor coupon={editing} lang={lang} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {/* Vincent (Eric 2026-02-06) — Modal "Gérer utilisateurs" pour ce coupon */}
      {managing && (
        <CouponUsersManager coupon={managing} lang={lang} onClose={() => setManaging(null)} />
      )}
    </div>
  );
}


function CouponUsersManager({ coupon, lang, onClose }) {
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/coupons/${coupon.id}/user-grants`, { withCredentials: true });
        setGrants(data.grants || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [coupon.id]);

  const toggleUser = async (uid) => {
    const current = grants.find(g => g.user_id === uid);
    if (!current) return;
    const willBeGranted = !current.is_granted;
    // Vincent #277 (Eric 2026-02-06) — Si on ACTIVE un coupon à durée limitée
    // pour un user qui en avait déjà bénéficié, demander si la date de début
    // est l'originale (granted_at d'avant) ou aujourd'hui.
    let startDateChoice = current.start_date_choice;
    if (willBeGranted && (coupon.duration_months || coupon.duration_weeks) && current.granted_at) {
      const original = new Date(current.granted_at).toLocaleDateString();
      const today = new Date().toLocaleDateString();
      const useOriginal = await confirmDialog(
        `Ce coupon a une durée limitée (${coupon.duration_months ? coupon.duration_months + " mois" : coupon.duration_weeks + " semaines"}).\n\n` +
        `Cet utilisateur en avait déjà bénéficié auparavant.\n\n` +
        `Date de début à utiliser :\n` +
        `  • OK = date d'origine (${original}) — la durée est déjà entamée\n` +
        `  • Annuler = aujourd'hui (${today}) — repartir à neuf`
      );
      startDateChoice = useOriginal ? "original" : "today";
    }
    setGrants(prev => prev.map(g => {
      if (g.user_id !== uid) return g;
      if (willBeGranted && startDateChoice) {
        return { ...g, is_granted: true, start_date_choice: startDateChoice };
      }
      return { ...g, is_granted: willBeGranted };
    }));
  };
  const setDuration = (uid, value) => {
    const n = value === "" ? null : parseInt(value, 10);
    setGrants(prev => prev.map(g => g.user_id === uid ? { ...g, duration_months_override: (Number.isFinite(n) && n > 0) ? n : null } : g));
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        grants: grants
          .filter(g => g.is_granted)
          .map(g => ({ user_id: g.user_id, duration_months: g.duration_months_override })),
      };
      await axios.put(`${API}/api/coupons/${coupon.id}/user-grants`, payload, { withCredentials: true });
      toast.success(L(lang, `${payload.grants.length} utilisateur(s) mis à jour`, `${payload.grants.length} user(s) updated`));
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  const filtered = grants
    .filter(g => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (g.email || "").toLowerCase().includes(s)
          || (g.first_name || "").toLowerCase().includes(s)
          || (g.last_name || "").toLowerCase().includes(s);
    })
    // Vincent #277 (Eric 2026-02-06) — Liste alphabétique : d'abord les
    // bénéficiaires actuels (is_granted=true), puis les autres.
    .sort((a, b) => {
      if (a.is_granted !== b.is_granted) return a.is_granted ? -1 : 1;
      const ka = `${a.last_name || ""} ${a.first_name || ""} ${a.email || ""}`.toLowerCase();
      const kb = `${b.last_name || ""} ${b.first_name || ""} ${b.email || ""}`.toLowerCase();
      return ka.localeCompare(kb);
    });
  const grantedCount = grants.filter(g => g.is_granted).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="coupon-users-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="coupon-users-modal">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-violet-50 to-transparent flex items-start justify-between">
          <div>
            <h2 className="font-bold text-lg flex items-center gap-2">
              <Users size={20} className="text-violet-600" />
              {L(lang, `Utilisateurs · ${coupon.code}`, `Users · ${coupon.code}`)}
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              {L(lang,
                `Coche les utilisateurs à qui appliquer ce coupon (${coupon.discount_percent}% off). Durée par défaut : ${coupon.duration_months ? coupon.duration_months + " mois" : coupon.duration_weeks ? coupon.duration_weeks + " semaines" : "illimité"}. Override possible par user.`,
                `Check users who should get this coupon (${coupon.discount_percent}% off). Default duration: ${coupon.duration_months ? coupon.duration_months + " months" : coupon.duration_weeks ? coupon.duration_weeks + " weeks" : "forever"}. Per-user override available.`)}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="coupon-users-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 border-b bg-zinc-50">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={L(lang, "Filtrer par nom ou email…", "Filter by name or email…")}
            className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm"
            data-testid="coupon-users-search"
          />
          <p className="text-xs text-zinc-500 mt-1">
            {L(lang, `${grantedCount} sélectionné(s) sur ${grants.length}`, `${grantedCount} of ${grants.length} selected`)}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <p className="text-sm text-zinc-400 text-center py-8">{L(lang, "Chargement…", "Loading…")}</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-8">{L(lang, "Aucun utilisateur", "No users")}</p>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map(g => (
                <li key={g.user_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${g.is_granted ? "border-violet-300 bg-violet-50" : "border-zinc-200 bg-white"}`} data-testid={`coupon-grant-row-${g.user_id}`}>
                  <input
                    type="checkbox"
                    checked={g.is_granted}
                    onChange={() => toggleUser(g.user_id)}
                    className="h-4 w-4 rounded border-violet-400 text-violet-600 focus:ring-violet-500"
                    data-testid={`coupon-grant-toggle-${g.user_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate">
                      {[g.first_name, g.last_name].filter(Boolean).join(" ") || g.email}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{g.email} · {g.org_role || "—"}</div>
                  </div>
                  {g.is_granted && (
                    <div className="flex items-center gap-1 text-xs">
                      <input
                        type="number"
                        value={g.duration_months_override ?? ""}
                        onChange={e => setDuration(g.user_id, e.target.value)}
                        min="1"
                        max="120"
                        placeholder={String(coupon.duration_months || "∞")}
                        className="w-16 px-2 py-1 border border-violet-200 rounded text-xs"
                        title={L(lang, "Durée en mois (vide = défaut coupon)", "Duration in months (empty = coupon default)")}
                      />
                      <span className="text-zinc-500">{L(lang, "mois", "mo")}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-zinc-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded border border-zinc-300 hover:bg-zinc-100" data-testid="coupon-users-cancel">
            {L(lang, "Annuler", "Cancel")}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="coupon-users-save"
          >
            <CheckCircle size={16} />
            {saving ? L(lang, "Sauvegarde…", "Saving…") : L(lang, `Enregistrer (${grantedCount})`, `Save (${grantedCount})`)}
          </button>
        </div>
      </div>
    </div>
  );
}


function CouponEditor({ coupon, lang, onClose, onSaved }) {
  const isNew = !coupon.id;
  const [code, setCode] = useState(coupon.code || "");
  const [name, setName] = useState(coupon.name || "");
  const [discount, setDiscount] = useState(coupon.discount_percent || 10);
  const [unlimited, setUnlimited] = useState(!coupon.duration_months && !coupon.duration_weeks);
  // Vincent #277 (Eric 2026-02-06) — Sélecteur d'unité : mois OU semaines.
  const [durationUnit, setDurationUnit] = useState(coupon.duration_weeks ? "weeks" : "months");
  const [duration, setDuration] = useState(coupon.duration_weeks || coupon.duration_months || 3);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!code.trim() || !name.trim()) { toast.error(L(lang, "Code et nom requis", "Code and name required")); return; }
    if (discount <= 0 || discount > 100) { toast.error(L(lang, "Réduction entre 0.01 et 100", "Discount must be 0.01–100")); return; }
    setSaving(true);
    try {
      const payload = {
        code: code.trim(),
        name: name.trim(),
        discount_percent: Number(discount),
        duration_months: (unlimited || durationUnit !== "months") ? null : Number(duration),
        duration_weeks: (unlimited || durationUnit !== "weeks") ? null : Number(duration),
      };
      if (isNew) {
        await axios.post(`${API}/api/coupons`, payload, { withCredentials: true });
        toast.success(L(lang, "Coupon créé", "Coupon created"));
      } else {
        await axios.patch(`${API}/api/coupons/${coupon.id}`, payload, { withCredentials: true });
        toast.success(L(lang, "Coupon mis à jour", "Coupon updated"));
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Échec", "Failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="coupon-editor-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{isNew ? L(lang, "Nouveau coupon", "New coupon") : L(lang, "Modifier coupon", "Edit coupon")}</h2>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
        </header>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">{L(lang, "Code *", "Code *")}</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={!isNew}
              className="w-full px-3 py-2 border border-zinc-300 rounded font-mono uppercase text-sm disabled:bg-zinc-100"
              placeholder="WELCOME20"
              data-testid="coupon-code-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">{L(lang, "Nom *", "Name *")}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="coupon-name-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">{L(lang, "Réduction (%) *", "Discount (%) *")}</label>
            <input type="number" step="0.5" min="0.01" max="100" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="coupon-discount-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">{L(lang, "Durée", "Duration")}</label>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1 text-sm">
                <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} data-testid="coupon-unlimited-toggle" />
                {L(lang, "Illimité", "Unlimited / Forever")}
              </label>
              {!unlimited && (
                <>
                  <input
                    type="number"
                    min="1"
                    max={durationUnit === "weeks" ? "520" : "120"}
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-24 px-2 py-1 border border-zinc-300 rounded text-sm"
                    data-testid="coupon-duration-input"
                  />
                  <select
                    value={durationUnit}
                    onChange={(e) => setDurationUnit(e.target.value)}
                    className="px-2 py-1 border border-zinc-300 rounded text-sm"
                    data-testid="coupon-duration-unit"
                  >
                    <option value="weeks">{L(lang, "semaines", "weeks")}</option>
                    <option value="months">{L(lang, "mois", "months")}</option>
                  </select>
                </>
              )}
            </div>
          </div>
        </div>
        <footer className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200" data-testid="coupon-cancel">{L(lang, "Annuler", "Cancel")}</button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 text-sm bg-[#002FA7] text-white rounded font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="coupon-save">
            <FloppyDisk size={14} /> {saving ? L(lang, "Enregistrement…", "Saving…") : L(lang, "Enregistrer", "Save")}
          </button>
        </footer>
      </div>
    </div>
  );
}
